"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleQuestionMark as CircleHelp } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "How do fixed interest rate guarantees work in a MYGA?",
  answer: "A Multi-Year Guaranteed Annuity, or MYGA, is a type of fixed annuity issued by an insurance company. It provides a stated interest rate for a specified guarantee period, subject to the terms of the contract.\n\nAt the end of the guarantee period, the contract may provide options such as continuing the annuity, selecting a new available guarantee period, withdrawing the contract value, or completing a qualifying exchange to another annuity. Available options depend on the contract.\n\nGuarantees are subject to the terms of the annuity contract and the claims-paying ability of the issuing insurance company."
}, {
  id: "item-2",
  question: "How do MYGAs compare directly to bank Certificates of Deposit (CDs)?",
  answer: "Both can provide a stated interest rate for a specified period, but they are different products.\n\nA bank CD is a deposit product and may be insured by the FDIC within applicable limits when held at an FDIC-insured bank.\n\nA MYGA is an insurance contract issued by an insurance company. It is not a bank deposit and is not FDIC-insured. Its guarantees are backed by the claims-paying ability of the issuing insurance company.\n\nMYGAs and CDs can also differ in tax treatment, withdrawal provisions, surrender charges, maturity options, and other terms."
}, {
  id: "item-3",
  question: "What are the tax-deferral advantages of a non-qualified MYGA?",
  answer: "Interest inside a nonqualified MYGA generally grows tax-deferred, meaning federal income tax is generally not due on the growth while it remains in the contract.\n\nWhen money is withdrawn from a nonqualified annuity before the annuity starting date, federal tax rules generally treat earnings as coming out before the owner's investment in the contract for contracts purchased after August 13, 1982.\n\nTax treatment can vary depending on how the annuity is owned, funded, and distributed. DJTD Consulting does not provide tax advice."
}, {
  id: "item-4",
  question: "Can I access money during the MYGA guarantee period?",
  answer: "Access depends on the specific contract. Some MYGAs allow limited withdrawals without a surrender charge, while withdrawals above the permitted amount may be subject to surrender charges or other contract adjustments.\n\nA withdrawal may also create taxable income. If a taxable annuity distribution occurs before age 59½, an additional 10% federal tax may apply to the taxable portion unless an exception applies.\n\nReview the specific contract's withdrawal provisions and surrender schedule before purchasing or taking a withdrawal."
}, {
  id: "item-5",
  question: "What happens when the MYGA guarantee period ends?",
  answer: "Options at the end of a MYGA guarantee period depend on the specific insurance contract. Depending on the contract, options may include selecting another available guarantee period, withdrawing the contract value, receiving an available payout option, or moving the value to another eligible annuity.\n\nA properly structured qualifying exchange from one annuity contract to another may qualify for tax-deferred treatment under Internal Revenue Code Section 1035. Tax consequences can depend on the transaction and individual circumstances.\n\nReview the contract before the end of the guarantee period because renewal provisions, available options, deadlines, and interest rates vary by insurance company and contract."
}];
export default function Mygas() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="mygas" data-nav="light" className="bg-background py-20 md:py-28 text-foreground transition-colors">
      <div className="container mx-auto px-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          {/* Header */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center">
            <div className="inline-flex items-center justify-center">
              <Badge variant="outline" className="border-primary/20 bg-secondary/60 text-foreground font-semibold uppercase tracking-wider text-xs px-3 py-1 rounded-full mb-4">
                Annuity Basics
              </Badge>
            </div>

            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Multi-Year Guarantee Annuities (MYGAs)
            </h2>

            <div className="mx-auto mt-4 mb-6 flex items-center justify-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              <div className="h-px w-10 bg-border" />
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              <div className="h-px w-10 bg-border" />
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              <div className="h-px w-10 bg-border" />
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            </div>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Lock in a guaranteed fixed return for a set term with tax-deferred growth and steady principal protection.
            </p>
          </motion.div>

          {/* Accordion Q&A Plinths */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} className="mt-12">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30 hover:shadow-sm px-6 py-1 overflow-hidden">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-card-foreground text-left py-5 hover:no-underline hover:text-primary transition-colors">
                    <span className="flex items-start gap-3">
                      <CircleHelp className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 pr-2 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>

          {/* Clarity Guidance Note */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.2
        }} className="mt-12 rounded-xl border border-border bg-secondary/30 p-6">
            <div className="flex items-start gap-3.5">
              <div className="rounded-full bg-primary/10 p-2.5 text-primary shrink-0">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <h3 className="font-default text-base font-semibold text-foreground">
                  Need a personalized rate comparison?
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  We review multi-year fixed options across top-rated carriers with no sales pressure.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}